import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  dataTableHeadClass,
  DataTablePanel,
  PageShell,
} from '../../components/DataTablePanel';
import { FilterField, ListFiltersBar } from '../../components/ListFiltersBar';
import { SortableTh, sortItems, useTableSort } from '../../components/SortableTh';
import {
  Badge,
  Button,
  Callout,
  Card,
  DateInput,
  ListEmptyState,
  LoadingSpinner,
  LinkChainIcon,
  Select,
  Switch,
} from '../../components/ui';
import { ApiError, api } from '../../services/api';
import { t } from '../../i18n';
import { CustomerLookupItem } from '../../types/customer';
import { UnallocatedPaymentsReport } from '../../types/reports';
import { formatDate, formatMoney } from '../../utils/format';
import { AutoAllocatePreviewModal } from '../payments/AutoAllocatePreviewModal';
import { useAutoAllocate } from '../payments/useAutoAllocate';
import { ReportExportButtons } from './ReportExportButtons';
import { exportUnallocatedPayments, yesNo } from './exportReports';

const COLUMN_WIDTHS = [
  '7%',
  '13%',
  '13%',
  '11%',
  '11%',
  '10%',
  '10%',
  '12%',
  '13%',
];

function ColumnGroup() {
  return (
    <colgroup>
      {COLUMN_WIDTHS.map((width, index) => (
        <col key={index} style={{ width }} />
      ))}
    </colgroup>
  );
}

const sortGetters: Record<
  string,
  (
    row: UnallocatedPaymentsReport['data'][number],
  ) => string | number | boolean | null | undefined
> = {
  paymentDate: (r) => r.paymentDate,
  customer: (r) => r.customer.name,
  paymentTotal: (r) => r.totals.total,
  allocatedTotal: (r) => r.allocated.total,
  unallocatedInvoice: (r) => r.unallocated.invoice,
  unallocatedCash: (r) => r.unallocated.cash,
  unallocatedTotal: (r) => r.unallocated.total,
};

export function UnallocatedPaymentsPage() {
  const labels = t().reports;
  const [customers, setCustomers] = useState<CustomerLookupItem[]>([]);
  const [customerId, setCustomerId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [report, setReport] = useState<UnallocatedPaymentsReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { sort, toggleSort } = useTableSort('paymentDate', 'desc');
  const [reloadToken, setReloadToken] = useState(0);
  const allocate = useAutoAllocate(() => ({
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    customerId: customerId ? Number(customerId) : undefined,
    activeOnly,
  }));

  const displayRows = useMemo(
    () => (report ? sortItems(report.data, sort, sortGetters) : []),
    [report, sort],
  );
  const tableRef = useRef<HTMLTableElement>(null);
  const [scrollbarGutter, setScrollbarGutter] = useState(0);

  useLayoutEffect(() => {
    const scroller = tableRef.current?.parentElement;
    if (!scroller) return;
    const measure = () => {
      setScrollbarGutter(scroller.offsetWidth - scroller.clientWidth);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(scroller);
    return () => observer.disconnect();
  }, [displayRows]);

  useEffect(() => {
    void api
      .getCustomerLookup({ active: 'true' })
      .then(setCustomers);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await api.getUnallocatedPaymentsReport({
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
          customerId: customerId ? Number(customerId) : undefined,
          activeOnly,
        });
        if (!cancelled) setReport(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : t().common.error);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [dateFrom, dateTo, customerId, activeOnly, reloadToken]);

  function handleExport(format: 'excel' | 'pdf') {
    if (!report) return;
    exportUnallocatedPayments({
      report,
      rows: displayRows,
      meta: [
        { label: labels.dateFrom, value: formatDate(dateFrom || null) },
        { label: labels.dateTo, value: formatDate(dateTo || null) },
        {
          label: t().common.customer,
          value:
            customers.find((c) => String(c.id) === customerId)?.name ??
            labels.allCustomers,
        },
        { label: labels.activeOnly, value: yesNo(activeOnly) },
      ],
      format,
    });
  }

  async function handleApplyAllocate() {
    const applied = await allocate.applySelected();
    if (applied) setReloadToken((value) => value + 1);
  }

  return (
    <PageShell>
      <ListFiltersBar
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              loading={allocate.previewing}
              disabled={loading || allocate.previewing}
              onClick={() => void allocate.openPreview()}
            >
              <LinkChainIcon className="h-3.5 w-3.5" />
              {labels.autoAllocate}
            </Button>
            <ReportExportButtons
              disabled={!report}
              onExcel={() => handleExport('excel')}
              onPdf={() => handleExport('pdf')}
            />
          </div>
        }
      >
        <FilterField label={labels.dateFrom}>
          <DateInput
            value={dateFrom}
            showClearButton
            showQuickFill={false}
            showFormatHint={false}
            placeholder={labels.dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </FilterField>
        <FilterField label={labels.dateTo}>
          <DateInput
            value={dateTo}
            showClearButton
            showQuickFill={false}
            showFormatHint={false}
            placeholder={labels.dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </FilterField>
        <FilterField label={t().common.customer}>
          <Select
            value={customerId}
            size="sm"
            options={[
              { value: '', label: labels.allCustomers },
              ...customers.map((c) => ({ value: String(c.id), label: c.name })),
            ]}
            onChange={(e) => setCustomerId(e.target.value)}
          />
        </FilterField>
        <FilterField label={labels.activeOnly}>
          <div className="flex h-8 items-center rounded-lg border border-line bg-surface-card px-3">
            <Switch
              checked={activeOnly}
              onChange={(e) => setActiveOnly(e.target.checked)}
              aria-label={labels.activeOnly}
              size="sm"
            />
          </div>
        </FilterField>
      </ListFiltersBar>

      {allocate.success ? (
        <Callout variant="success" className="shrink-0">
          {allocate.success}
        </Callout>
      ) : null}

      {error || allocate.error ? (
        <Callout variant="danger" className="shrink-0">
          {error || allocate.error}
        </Callout>
      ) : null}

      {loading || !report ? (
        <LoadingSpinner message={t().common.loading} />
      ) : (
        <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4">
          <div className="grid shrink-0 gap-4 md:grid-cols-3">
            <SummaryCard
              title={labels.paymentAmount}
              totals={report.totals.payment}
            />
            <SummaryCard
              title={labels.allocatedAmount}
              totals={report.totals.allocated}
            />
            <SummaryCard
              title={labels.unallocatedAmount}
              totals={report.totals.unallocated}
              emphasize
            />
          </div>

          <DataTablePanel
            className="min-h-0 min-w-0 flex-1"
            footer={
              displayRows.length === 0 ? null : (
                <div
                  className="border-t border-line bg-surface-muted text-sm font-medium"
                  style={{ paddingRight: scrollbarGutter }}
                >
                  <table className="w-full table-fixed">
                    <ColumnGroup />
                    <tbody>
                      <tr>
                        <td className="px-3 py-2">{t().common.total}</td>
                        <td />
                        <td />
                        <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums">
                          {formatMoney(report.totals.payment.total)}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums">
                          {formatMoney(report.totals.allocated.total)}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums">
                          {formatMoney(report.totals.unallocated.invoice)}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums">
                          {formatMoney(report.totals.unallocated.cash)}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums">
                          {formatMoney(report.totals.unallocated.total)}
                        </td>
                        <td />
                      </tr>
                    </tbody>
                  </table>
                </div>
              )
            }
          >
            {displayRows.length === 0 ? (
              <ListEmptyState title={t().common.empty} />
            ) : (
              <table ref={tableRef} className="w-full table-fixed text-sm">
                <ColumnGroup />
                <thead className={dataTableHeadClass}>
                  <tr>
                    <SortableTh
                      sortKey="paymentDate"
                      sort={sort}
                      onSort={toggleSort}
                    >
                      {t().common.date}
                    </SortableTh>
                    <SortableTh
                      sortKey="customer"
                      sort={sort}
                      onSort={toggleSort}
                    >
                      {t().common.customer}
                    </SortableTh>
                    <th className="px-3 py-2 text-left font-semibold">
                      {t().payments.description}
                    </th>
                    <SortableTh
                      sortKey="paymentTotal"
                      sort={sort}
                      onSort={toggleSort}
                      align="right"
                      wrap
                    >
                      {labels.paymentAmount}
                    </SortableTh>
                    <SortableTh
                      sortKey="allocatedTotal"
                      sort={sort}
                      onSort={toggleSort}
                      align="right"
                      wrap
                    >
                      {labels.allocatedAmount}
                    </SortableTh>
                    <SortableTh
                      sortKey="unallocatedInvoice"
                      sort={sort}
                      onSort={toggleSort}
                      align="right"
                    >
                      {t().financial.invoice}
                    </SortableTh>
                    <SortableTh
                      sortKey="unallocatedCash"
                      sort={sort}
                      onSort={toggleSort}
                      align="right"
                    >
                      {t().financial.cash}
                    </SortableTh>
                    <SortableTh
                      sortKey="unallocatedTotal"
                      sort={sort}
                      onSort={toggleSort}
                      align="right"
                      wrap
                    >
                      {labels.unallocatedAmount}
                    </SortableTh>
                    <th className="px-3 py-2 text-left font-semibold">
                      {labels.allocationCount}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {displayRows.map((row) => (
                    <tr key={row.id} className="border-t border-line">
                      <td className="whitespace-nowrap px-3 py-2">
                        {formatDate(row.paymentDate)}
                      </td>
                      <td className="px-3 py-2">
                        <Link
                          to={`/customers/${row.customer.id}`}
                          className="block truncate text-brand hover:underline"
                          title={row.customer.name}
                        >
                          {row.customer.name}
                        </Link>
                      </td>
                      <td className="px-3 py-2">
                        <Link
                          to={`/payments/${row.id}`}
                          className="block truncate text-brand hover:underline"
                          title={row.description || undefined}
                        >
                          {row.description || `Πληρωμή #${row.id}`}
                        </Link>
                      </td>
                      <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums">
                        {formatMoney(row.totals.total)}
                      </td>
                      <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums">
                        {formatMoney(row.allocated.total)}
                      </td>
                      <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums">
                        {formatMoney(row.unallocated.invoice)}
                      </td>
                      <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums">
                        {formatMoney(row.unallocated.cash)}
                      </td>
                      <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums font-medium">
                        {formatMoney(row.unallocated.total)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2">
                        <Badge
                          variant={
                            row.isFullyUnallocated ? 'warning' : 'secondary'
                          }
                        >
                          {row.isFullyUnallocated
                            ? labels.fullyUnallocated
                            : `${row.allocationCount}`}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </DataTablePanel>
        </div>
      )}

      <AutoAllocatePreviewModal
        isOpen={allocate.previewOpen}
        preview={allocate.preview}
        applying={allocate.applying}
        selectedKeys={allocate.selectedKeys}
        previewKeys={allocate.previewKeys}
        allSelected={allocate.allSelected}
        someSelected={allocate.someSelected}
        allowRematch={allocate.allowRematch}
        replaceExisting={allocate.replaceExisting}
        previewing={allocate.previewing}
        onReplaceExistingChange={(next) =>
          void allocate.changeReplaceExisting(next)
        }
        onClose={allocate.closePreview}
        onToggle={allocate.toggleSelection}
        onSelectAll={(keys) => allocate.setSelectedKeys(new Set(keys))}
        onApply={() => void handleApplyAllocate()}
      />
    </PageShell>
  );
}

function SummaryCard({
  title,
  totals,
  emphasize,
}: {
  title: string;
  totals: { invoice: string; cash: string; total: string };
  emphasize?: boolean;
}) {
  return (
    <Card className="p-4 text-sm">
      <div className="font-semibold text-ink">{title}</div>
      <div className="mt-3 space-y-1">
        <div className="flex justify-between">
          <span>{t().financial.invoice}</span>
          <span className="tabular-nums">{formatMoney(totals.invoice)}</span>
        </div>
        <div className="flex justify-between">
          <span>{t().financial.cash}</span>
          <span className="tabular-nums">{formatMoney(totals.cash)}</span>
        </div>
        <div
          className={`flex justify-between font-semibold ${emphasize ? 'text-brand' : ''}`}
        >
          <span>{t().financial.grandTotal}</span>
          <span className="tabular-nums">{formatMoney(totals.total)}</span>
        </div>
      </div>
    </Card>
  );
}
